import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { en, type Dictionary, type MessageKey } from './en';
import { ru } from './ru';
import { formatMessage } from './translate';
import { LOCALES, type Locale, type TranslateVars } from './types';

const DICTIONARIES: Record<Locale, Dictionary> = { en, ru };

export const LOCALE_STORAGE_KEY = 'tms.locale';

const isLocale = (value: unknown): value is Locale =>
  typeof value === 'string' && (LOCALES as readonly string[]).includes(value);

/** An explicit choice wins; otherwise the browser's language decides. */
export const detectLocale = (): Locale => {
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    // Storage may be unavailable; fall through to the browser language.
  }

  const preferred = [navigator.language, ...(navigator.languages ?? [])];
  return preferred.some((tag) => tag?.toLowerCase().startsWith('ru')) ? 'ru' : 'en';
};

export interface LocaleContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: MessageKey, vars?: TranslateVars) => string;
}

export const LocaleContext = createContext<LocaleContextValue | null>(null);

export const LocaleProvider = ({ children }: { children: ReactNode }) => {
  const [locale, setLocaleState] = useState<Locale>(() => detectLocale());

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, next);
    } catch {
      // A preference we cannot persist is still applied for this session.
    }
  }, []);

  const t = useCallback(
    (key: MessageKey, vars?: TranslateVars) =>
      formatMessage(locale, DICTIONARIES[locale][key] ?? en[key], vars),
    [locale],
  );

  const value = useMemo<LocaleContextValue>(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};
